
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ProFeatureGate } from '@/components/common/ProFeatureGate';
import { AuthProvider } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import { ProjectSelectionProvider } from '@/contexts/ProjectSelectionContext';
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import GoogleAnalytics from '@/components/GoogleAnalytics';
import GoogleIndexingHelper from '@/components/seo/GoogleIndexingHelper';

// Critical page loaded immediately for FCP
import Index from '@/pages/Index';

// Lazy load with retry to handle transient dev-server/module fetch failures
const lazyWithRetry = <T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  retries = 2,
  delayMs = 400
) =>
  lazy(async () => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await importer();
      } catch (error) {
        lastError = error;
        const message = error instanceof Error ? error.message : String(error);
        const isTransientImportError =
          message.includes('Failed to fetch dynamically imported module') ||
          message.includes('Importing a module script failed') ||
          message.includes('fetch');

        if (!isTransientImportError || attempt === retries) {
          throw error;
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
      }
    }

    throw lastError;
  });

// Lazy load all other pages for better FCP
const Auth = lazyWithRetry(() => import('@/pages/Auth'));
const OAuthConsent = lazyWithRetry(() => import('@/pages/OAuthConsent'));
const McpDocs = lazyWithRetry(() => import('@/pages/docs/McpDocs'));
const MobileApp = lazyWithRetry(() => import('@/pages/MobileApp'));
const Dashboard = lazyWithRetry(() => import('@/pages/Dashboard'));
const MyDay = lazyWithRetry(() => import('@/pages/MyDay'));
const Projects = lazyWithRetry(() => import('@/pages/Projects'));
const ProjectDetail = lazyWithRetry(() => import('@/pages/ProjectDetail'));
const AIPredictive = lazyWithRetry(() => import('@/pages/AIPredictive'));
const VirtualAgent = lazyWithRetry(() => import('@/pages/VirtualAgent'));
const Analytics = lazyWithRetry(() => import('@/pages/Analytics'));
const Resources = lazyWithRetry(() => import('@/pages/Resources'));
const Automation = lazyWithRetry(() => import('@/pages/Automation'));
const Innovation = lazyWithRetry(() => import('@/pages/Innovation'));
const Training = lazyWithRetry(() => import('@/pages/Training'));
const CourseViewer = lazyWithRetry(() => import('@/pages/CourseViewer'));
const VideoCourseViewer = lazyWithRetry(() => import('@/pages/VideoCourseViewer'));
const ProjectManagement = lazyWithRetry(() => import('@/pages/ProjectManagement'));
const ProjectPlanner = lazyWithRetry(() => import('@/pages/ProjectPlanner'));
const BulkImport = lazyWithRetry(() => import('@/pages/BulkImport'));
const AdminLogs = lazyWithRetry(() => import('@/pages/admin/AdminLogs'));
const Settings = lazyWithRetry(() => import('@/pages/Settings'));
const Team = lazyWithRetry(() => import('@/pages/Team'));
const UserManagement = lazyWithRetry(() => import('@/pages/admin/UserManagement'));
const WeeklyReportsPage = lazyWithRetry(() => import('@/pages/WeeklyReportsPage'));
const CreatePresentation = lazyWithRetry(() => import('@/pages/CreatePresentation'));
const ProjectMembers = lazyWithRetry(() => import('@/pages/ProjectMembers'));

// Public pages
const Features = lazyWithRetry(() => import('@/pages/Features'));
const Pricing = lazyWithRetry(() => import('@/pages/Pricing'));
const Integrations = lazyWithRetry(() => import('@/pages/Integrations'));
const UseCases = lazyWithRetry(() => import('@/pages/UseCases'));
const UserGuides = lazyWithRetry(() => import('@/pages/UserGuides'));
const Blog = lazyWithRetry(() => import('@/pages/Blog'));
const BlogResources = lazyWithRetry(() => import('@/pages/resources/Blog'));
const Comparativas = lazyWithRetry(() => import('@/pages/Comparativas'));
const MondayComparison = lazyWithRetry(() => import('@/pages/comparativas/Monday'));
const AsanaComparison = lazyWithRetry(() => import('@/pages/comparativas/Asana'));
const TrelloComparison = lazyWithRetry(() => import('@/pages/comparativas/Trello'));
const JiraComparison = lazyWithRetry(() => import('@/pages/comparativas/Jira'));
const ClickUpComparison = lazyWithRetry(() => import('@/pages/comparativas/ClickUp'));
const NotionComparison = lazyWithRetry(() => import('@/pages/comparativas/Notion'));
const ApiDocs = lazyWithRetry(() => import('@/pages/resources/ApiDocs'));
const FreeAIToolsGuide = lazyWithRetry(() => import('@/pages/resources/FreeAIToolsGuide'));
const AboutUs = lazyWithRetry(() => import('@/pages/AboutUs'));
const Contact = lazyWithRetry(() => import('@/pages/Contact'));
const PrivacyPolicy = lazyWithRetry(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('@/pages/TermsOfService'));
const CookiePolicy = lazyWithRetry(() => import('@/pages/CookiePolicy'));
const ArticleDetail = lazyWithRetry(() => import('@/pages/ArticleDetail'));
const UserManualPage = lazyWithRetry(() => import('@/pages/UserManualPage'));
const OrchestratorPage = lazyWithRetry(() => import('@/pages/OrchestratorPage'));
const MarketingAgentPage = lazyWithRetry(() => import('@/pages/MarketingAgentPage'));
const CommunicationPage = lazyWithRetry(() => import('@/pages/CommunicationPage'));
const AIPredictivePublic = lazyWithRetry(() => import('@/pages/product/AIPredictive'));
const AIEthics = lazyWithRetry(() => import('@/pages/AIEthics'));
const PrivacySecurity = lazyWithRetry(() => import('@/pages/PrivacySecurity'));
const ProjectGenetics = lazyWithRetry(() => import('@/pages/ProjectGenetics'));
const TaskTrack = lazyWithRetry(() => import('@/pages/TaskTrack'));
const ActivitySummary = lazyWithRetry(() => import('@/pages/ActivitySummary'));
const AlertsConfigPage = lazyWithRetry(() => import('@/pages/AlertsConfigPage'));
const ExecutiveReport = lazyWithRetry(() => import('@/pages/ExecutiveReport'));
const OwnerExecutiveDashboard = lazyWithRetry(() => import('@/pages/OwnerExecutiveDashboard'));
const NotificationFlowsPage = lazyWithRetry(() => import('@/pages/NotificationFlowsPage'));
const ResetPassword = lazyWithRetry(() => import('@/pages/ResetPassword'));
const NotFound = lazyWithRetry(() => import('@/pages/NotFound'));

// SEO Landing Pages
const DiagramaGanttGratis = lazyWithRetry(() => import('@/pages/seo/DiagramaGanttGratis'));
const AnalisisFoda = lazyWithRetry(() => import('@/pages/seo/AnalisisFoda'));
const SoftwareGestionProyectosGratis = lazyWithRetry(() => import('@/pages/seo/SoftwareGestionProyectosGratis'));
const AlternativaMondayGratis = lazyWithRetry(() => import('@/pages/seo/AlternativaMondayGratis'));
const GestionProyectosPymesMexico = lazyWithRetry(() => import('@/pages/seo/GestionProyectosPymesMexico'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const AppRuntimeFallback = () => (
  <div className="min-h-screen flex items-center justify-center px-6">
    <div className="max-w-md text-center space-y-3">
      <h1 className="text-xl font-semibold text-foreground">No se pudo cargar esta vista</h1>
      <p className="text-sm text-muted-foreground">
        Hubo un problema temporal cargando módulos. Intenta recargar en unos segundos.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        Reintentar
      </button>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos antes de considerar datos obsoletos
      gcTime: 10 * 60 * 1000, // 10 minutos en cache antes de limpiar
      refetchOnWindowFocus: false, // No re-fetch al cambiar de pestaña
      retry: 1, // Solo 1 reintento en caso de error
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <ProjectSelectionProvider>
                <TooltipProvider>
                <GoogleAnalytics />
                <ErrorBoundary fallback={<AppRuntimeFallback />}>
                  <Router>
                    <GoogleIndexingHelper />
                     <Routes>
                  {/* Rutas públicas */}
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Suspense fallback={<PageLoader />}><Auth /></Suspense>} />
                  <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
                  <Route path="/.lovable/oauth/consent" element={<Suspense fallback={<PageLoader />}><OAuthConsent /></Suspense>} />
                  <Route path="/docs/mcp" element={<Suspense fallback={<PageLoader />}><McpDocs /></Suspense>} />
                  <Route path="/app-movil" element={<Suspense fallback={<PageLoader />}><MobileApp /></Suspense>} />
                  
                  {/* Redirects 301 para URLs antiguas indexadas */}
                  <Route path="/software-gestion-proyectos-mexico/" element={<Navigate to="/software-gestion-proyectos-gratis" replace />} />
                  <Route path="/resources/api-doc/" element={<Navigate to="/resources/api-docs" replace />} />
                  <Route path="/metodologias-agiles-herramientas/" element={<Navigate to="/blog" replace />} />
                  <Route path="/company/careers/" element={<Navigate to="/about-us" replace />} />
                  <Route path="/product/use-cases/" element={<Navigate to="/use-cases" replace />} />
                  <Route path="/dashboard-proyectos-tiempo-real" element={<Navigate to="/features" replace />} />
                  <Route path="/resources/community/" element={<Navigate to="/blog" replace />} />
                  
                  {/* Rutas protegidas - requieren autenticación */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="/dashboard" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
                    <Route path="/my-day" element={<Suspense fallback={<PageLoader />}><MyDay /></Suspense>} />
                    <Route path="/executive-report" element={<Suspense fallback={<PageLoader />}><ExecutiveReport /></Suspense>} />
                    <Route path="/owner-executive" element={<Suspense fallback={<PageLoader />}><OwnerExecutiveDashboard /></Suspense>} />
                    <Route path="/projects" element={<Suspense fallback={<PageLoader />}><Projects /></Suspense>} />
                    <Route path="/project/:projectId" element={<Suspense fallback={<PageLoader />}><ProjectDetail /></Suspense>} />
                    <Route path="/project-planner" element={<Suspense fallback={<PageLoader />}><ProjectPlanner /></Suspense>} />
                    <Route path="/bulk-import" element={<Suspense fallback={<PageLoader />}><BulkImport /></Suspense>} />
                    <Route path="/ai-predictive" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="ai_predictive" featureLabel="IA Predictiva"><AIPredictive /></ProFeatureGate></Suspense>} />
                    <Route path="/virtual-agent" element={<Suspense fallback={<PageLoader />}><VirtualAgent /></Suspense>} />
                    <Route path="/communication" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="communication" featureLabel="Comunicación Avanzada"><CommunicationPage /></ProFeatureGate></Suspense>} />
                    <Route path="/orchestrator" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="orchestrator" featureLabel="Orquestador de Agentes"><OrchestratorPage /></ProFeatureGate></Suspense>} />
                    <Route path="/marketing-agent" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="marketing_agent" featureLabel="Agente de Marketing"><MarketingAgentPage /></ProFeatureGate></Suspense>} />
                    <Route path="/analytics" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="analytics" featureLabel="Analytics Avanzados"><Analytics /></ProFeatureGate></Suspense>} />
                    <Route path="/resources" element={<Suspense fallback={<PageLoader />}><Resources /></Suspense>} />
                    <Route path="/automation" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="automation" featureLabel="Automatizaciones IA"><Automation /></ProFeatureGate></Suspense>} />
                    <Route path="/innovation" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="innovation" featureLabel="Innovación y ADN de Proyectos"><Innovation /></ProFeatureGate></Suspense>} />
                    <Route path="/genetics" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="genetics" featureLabel="Genética de Proyectos"><ProjectGenetics /></ProFeatureGate></Suspense>} />
                    <Route path="/genetics/:projectId" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="genetics" featureLabel="Genética de Proyectos"><ProjectGenetics /></ProFeatureGate></Suspense>} />
                    <Route path="/reports" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="reports_executive" featureLabel="Reportes Ejecutivos"><WeeklyReportsPage /></ProFeatureGate></Suspense>} />
                    <Route path="/create-presentation" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="presentations" featureLabel="Exportación de Presentaciones"><CreatePresentation /></ProFeatureGate></Suspense>} />
                    <Route path="/training" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="training" featureLabel="Entrenamiento IA Personalizado"><Training /></ProFeatureGate></Suspense>} />
                    <Route path="/course/:courseId" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="training" featureLabel="Entrenamiento IA Personalizado"><CourseViewer /></ProFeatureGate></Suspense>} />
                    <Route path="/video-course/:courseId" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="training" featureLabel="Entrenamiento IA Personalizado"><VideoCourseViewer /></ProFeatureGate></Suspense>} />
                    <Route path="/project-management/:projectId" element={<Suspense fallback={<PageLoader />}><ProjectManagement /></Suspense>} />
                    <Route path="/admin/logs" element={<Suspense fallback={<PageLoader />}><AdminLogs /></Suspense>} />
                    <Route path="/admin/users" element={<Suspense fallback={<PageLoader />}><UserManagement /></Suspense>} />
                    <Route path="/settings" element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
                    <Route path="/user-manual" element={<Suspense fallback={<PageLoader />}><UserManualPage /></Suspense>} />
                    <Route path="/team" element={<Suspense fallback={<PageLoader />}><Team /></Suspense>} />
                    <Route path="/project-members" element={<Suspense fallback={<PageLoader />}><ProjectMembers /></Suspense>} />
                    <Route path="/task-track" element={<Suspense fallback={<PageLoader />}><TaskTrack /></Suspense>} />
                    <Route path="/task-track/alarms" element={<Suspense fallback={<PageLoader />}><TaskTrack /></Suspense>} />
                    <Route path="/task-track/ai" element={<Suspense fallback={<PageLoader />}><TaskTrack /></Suspense>} />
                    <Route path="/task-track/activity-summary" element={<Suspense fallback={<PageLoader />}><ActivitySummary /></Suspense>} />
                    <Route path="/alerts" element={<Suspense fallback={<PageLoader />}><ProFeatureGate feature="alerts" featureLabel="Alertas Programadas"><AlertsConfigPage /></ProFeatureGate></Suspense>} />
                    <Route path="/notification-flows" element={<Suspense fallback={<PageLoader />}><NotificationFlowsPage /></Suspense>} />
                  </Route>
                  {/* Rutas públicas */}
                  <Route path="/features" element={<Suspense fallback={<PageLoader />}><Features /></Suspense>} />
                  <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><Pricing /></Suspense>} />
                  <Route path="/integrations" element={<Suspense fallback={<PageLoader />}><Integrations /></Suspense>} />
                  <Route path="/use-cases" element={<Suspense fallback={<PageLoader />}><UseCases /></Suspense>} />
                  <Route path="/user-guides" element={<Suspense fallback={<PageLoader />}><UserGuides /></Suspense>} />
                  <Route path="/blog" element={<Suspense fallback={<PageLoader />}><Blog /></Suspense>} />
                  <Route path="/blog/article/:id" element={<Suspense fallback={<PageLoader />}><ArticleDetail /></Suspense>} />
                  <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><ArticleDetail /></Suspense>} />
                  <Route path="/resources/blog" element={<Suspense fallback={<PageLoader />}><BlogResources /></Suspense>} />
                  <Route path="/comparativas" element={<Suspense fallback={<PageLoader />}><Comparativas /></Suspense>} />
                  <Route path="/comparativas/monday" element={<Suspense fallback={<PageLoader />}><MondayComparison /></Suspense>} />
                  <Route path="/comparativas/asana" element={<Suspense fallback={<PageLoader />}><AsanaComparison /></Suspense>} />
                  <Route path="/comparativas/trello" element={<Suspense fallback={<PageLoader />}><TrelloComparison /></Suspense>} />
                  <Route path="/comparativas/jira" element={<Suspense fallback={<PageLoader />}><JiraComparison /></Suspense>} />
                  <Route path="/comparativas/clickup" element={<Suspense fallback={<PageLoader />}><ClickUpComparison /></Suspense>} />
                  <Route path="/comparativas/notion" element={<Suspense fallback={<PageLoader />}><NotionComparison /></Suspense>} />
                  <Route path="/resources/api-docs" element={<Suspense fallback={<PageLoader />}><ApiDocs /></Suspense>} />
                  <Route path="/resources/free-ai-tools-guide" element={<Suspense fallback={<PageLoader />}><FreeAIToolsGuide /></Suspense>} />
                  <Route path="/about-us" element={<Suspense fallback={<PageLoader />}><AboutUs /></Suspense>} />
                  <Route path="/contact" element={<Suspense fallback={<PageLoader />}><Contact /></Suspense>} />
                  <Route path="/privacy-policy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
                  <Route path="/terms-of-service" element={<Suspense fallback={<PageLoader />}><TermsOfService /></Suspense>} />
                  <Route path="/cookie-policy" element={<Suspense fallback={<PageLoader />}><CookiePolicy /></Suspense>} />
                  {/* Ruta pública de IA Predictiva */}
                  <Route path="/product/ai-predictive" element={<Suspense fallback={<PageLoader />}><AIPredictivePublic /></Suspense>} />
                  <Route path="/ai-ethics" element={<Suspense fallback={<PageLoader />}><AIEthics /></Suspense>} />
                  <Route path="/privacy-security" element={<Suspense fallback={<PageLoader />}><PrivacySecurity /></Suspense>} />
                  {/* SEO Landing Pages */}
                  <Route path="/diagrama-gantt-online-gratis" element={<Suspense fallback={<PageLoader />}><DiagramaGanttGratis /></Suspense>} />
                  <Route path="/analisis-foda" element={<Suspense fallback={<PageLoader />}><AnalisisFoda /></Suspense>} />
                  <Route path="/software-gestion-proyectos-gratis" element={<Suspense fallback={<PageLoader />}><SoftwareGestionProyectosGratis /></Suspense>} />
                  <Route path="/alternativa-monday-gratis" element={<Suspense fallback={<PageLoader />}><AlternativaMondayGratis /></Suspense>} />
                  <Route path="/gestion-proyectos-pymes-mexico" element={<Suspense fallback={<PageLoader />}><GestionProyectosPymesMexico /></Suspense>} />
                  {/* Catch-all 404 */}
                  <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
                </Routes>
                  <Toaster />
                  </Router>
                </ErrorBoundary>
                </TooltipProvider>
              </ProjectSelectionProvider>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
